import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/FilePicker.css';
import '../../styles/MoveFileModal.css';
import Modal from '../Modal/Modal';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomButton from '../CustomElements/CustomButton';
import { handleUniversalChange } from '../../utils/formUtils';
import { useDispatch, useSelector } from 'react-redux';
import { initFilesFromServer } from '../../redux/actions/files.action';
import useTranslations from '../../customHook/useTranslations';
import translations from '../../content/PagesContent/FilesPages/filePageTranslations.json';

/**
 * Компонент выбора файла через модальное окно с деревом папок.
 *
 * @example
 * return (
 *   <FilePicker
 *     label="Акт приема-передачи *"
 *     formData={formData}
 *     setFormData={setFormData}
 *     fieldName="acceptanceCertName"
 *     fieldIdName="acceptanceCert"
 *   />
 * );
 *
 * @component
 * @param {Object} props - Свойства компонента.
 * @param {string} props.label - Название поля.
 * @param {string} props.placeholder - Текст по умолчанию, если файл не выбран.
 * @param {Object} props.formData - Текущее состояние формы.
 * @param {Function} props.setFormData - Функция для обновления состояния формы.
 * @param {string} props.fieldIdName - Имя поля для ID файла.
 * @param {string} props.fieldName - Имя поля для названия файла.
 * @returns {JSX.Element} - Компонент выбора файла.
 */
const ChevronRight = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const FilePicker = ({ label, placeholder, formData, setFormData, fieldIdName, fieldName }) => {
  // Инициализация dispatch для работы с Redux
  const dispatch = useDispatch();
  // получение словаря для статических данных в зависимости от выбранного языка
  const t = useTranslations(translations);
  // если не передан плейсхолдер формируем по умолчанию из словаря
  const resolvedPlaceholder = useMemo(() => {
    return placeholder || t?.filePlaceholder;
  }, [placeholder, t]);

  // Данные файлов и папок
  const files = useSelector((state) => state.files);
  // Состояние выбранного файла (если файл не выбран, значение пустое)
  const [selectedFile, setSelectedFile] = useState('');

  // Состояние для открытия/закрытия модального окна с деревом файлов
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Состояние развернутых папок (объект, где ключ — ID папки, а значение — её состояние: открыта/закрыта)
  const [expandedFolders, setExpandedFolders] = useState({});

  // Состояние для поиска файлов по имени
  const [searchTerm, setSearchTerm] = useState('');

  // Состояние для отслеживания наведения на кнопку (изменение цвета иконки)
  const [isHovered, setIsHovered] = useState(false);

  // Инициализация данных файлов и папок при загрузке компонента
  useEffect(() => {
    dispatch(initFilesFromServer());
  }, [dispatch]);

  /**
   * Закрывает модальное окно и сбрасывает выбранный файл в локальном стейте.
   */
  const onClose = () => {
    setSelectedFile('');
    setIsModalOpen(false);
    setSearchTerm('');
  };

  /**
   * Сбрасывает выбранный файл и очищает соответствующие поля в `formData` и локальном стейте.
   */
  const handleFileReset = (event) => {
    event.stopPropagation();
    handleUniversalChange(formData, setFormData, null, fieldIdName, { id: '', value: '' }, 'select');
    handleUniversalChange(formData, setFormData, null, fieldName, { value: '' }, 'select');
    setSelectedFile('');
  };

  /**
   * Подтверждает выбор файла и сохраняет его в `formData` и закрывает модальное окно.
   */
  const handleFileConfirm = () => {
    handleUniversalChange(formData, setFormData, null, fieldIdName, { value: selectedFile?.id }, 'select');
    handleUniversalChange(formData, setFormData, null, fieldName, { value: selectedFile?.name }, 'select');
    onClose();
  };

  /**
   * Разворачивает/сворачивает папку.
   * @param {string} folderId - ID папки.
   */
  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  /**
   * Выбирает и записывает файл в локальный стейт
   * @param {Object} file - Выбранный файл.
   */
  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  /**
   * Подсвечивает совпадения поискового запроса внутри строки.
   *
   * @param {string} text - Исходный текст, в котором ищем совпадения.
   * @param {string} query - Поисковый запрос для подсветки.
   * @returns {Array<string|JSX.Element>} - Массив строк и элементов <span className="highlight"> для отображения с подсветкой.
   */
  const highlightMatch = (text, query) => {
    // Если запрос пустой — возвращаем оригинальный текст без изменений
    if (!query) return text;

    // Разбиваем текст на части, используя RegExp с флагами 'g' и 'i'
    // - 'g' (global) — ищет все совпадения
    // - 'i' (ignoreCase) — не чувствителен к регистру
    // В скобках '()' — означает, что совпадения будут включены в результат (в parts)
    const parts = text.split(new RegExp(`(${query})`, 'gi'));

    // Преобразуем массив частей, подставляя <span> вокруг совпадающих участков
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  /**
   * Рекурсивно фильтрует дерево файлов и папок по поисковому термину.
   *
   * @param {Array} items - Массив элементов (файлов и папок).
   * @param {string} term - Строка поиска.
   * @returns {Array} - Отфильтрованный массив элементов, содержащих только подходящие папки/файлы.
   */
  const filterTree = (items, term) => {
    // Если строка поиска пуста или состоит только из пробелов — возвращаем оригинальное дерево
    if (!term.trim()) return items;

    // Применяем map к каждому элементу в массиве
    return (
      items
        .map((item) => {
          // Если текущий элемент — папка, проверяем её содержимое
          if (item.type === 'folder') {
            // Рекурсивно фильтруем дочерние элементы папки
            const filteredChildren = filterTree(item.children || [], term);

            // Если среди дочерних элементов есть совпадения — возвращаем копию папки с отфильтрованными детьми
            if (filteredChildren.length > 0) {
              return { ...item, children: filteredChildren };
            }
          }

          // Если имя текущего элемента содержит поисковую строку (без учёта регистра) — возвращаем этот элемент
          if (item.name.toLowerCase().includes(term.toLowerCase())) {
            return item;
          }

          // Иначе — элемент не подходит под фильтр, возвращаем null
          return null;
        })
        // Удаляем все null из результирующего массива
        .filter(Boolean)
    );
  };

  /**
   * Рекурсивно рендерит список папок и файлов.
   * @param {Array} items - Массив файлов и папок.
   * @param {number} level - Уровень вложенности.
   */
  const renderTree = (items, level = 0) =>
    items.map((item) => (
      <div key={item.id} style={{ marginLeft: level * 15 }}>
        <div
          className={`file-picker-item ${selectedFile?.id === item.id ? 'selected' : ''}`}
          onClick={item.type === 'file' ? () => handleFileSelect(item) : () => toggleFolder(item.id)}
        >
          <span className="file-manager-folder-label">
            <span className="folder-icon-wrapper">
              {item.type === 'folder' && (expandedFolders[item.id] ? <ChevronDown /> : <ChevronRight />)}
              <img
                src={`/image/${item.type === 'folder' ? 'folder.png' : 'sheet.png'}`}
                alt={item.type === 'folder' ? 'Folder' : 'File'}
                className="file-folder-icon img-size"
              />
            </span>
            <span className="folder-name">{highlightMatch(item.name, searchTerm)}</span>
          </span>
        </div>

        {expandedFolders[item.id] && item.children && (
          <div className="file-manager-nested-folders">{renderTree(item.children, level + 1)}</div>
        )}
      </div>
    ));

  return (
    <>
      <div className="file-picker">
        {/* Лейбл */}
        {label && <label className="file-picker-label">{label}</label>}

        {/* Поле выбора файла */}
        <div
          className="file-picker-container"
          onClick={() => {
            setIsModalOpen(true);
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span
            className={`file-picker-content ${formData?.[fieldName] || selectedFile ? 'has-value' : ''}`}
            title={formData?.[fieldName] || selectedFile?.name || resolvedPlaceholder}
          >
            {formData?.[fieldName] || selectedFile?.name || resolvedPlaceholder}
          </span>

          {/* Кнопка для открытия модального окна или сброса выбранного файла */}
          <div
            className="file-tree-button"
            onClick={(event) => {
              if (formData?.[fieldName]) handleFileReset(event);
            }}
          >
            {!formData?.[fieldName] ? (
              <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
              </svg>
            ) : (
              <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path
                  d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
                  fill={isHovered ? '#D30630' : '#000'}
                ></path>
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно с деревом файлов */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={onClose} width="45%" height="75%">
          <CustomContainer>
            <CustomTitle titleText={t?.selectFileTitle || 'ВЫБЕРИТЕ ФАЙЛ'} />
            <div style={{ position: 'relative' }}>
              <input
                id="submit_search_file"
                autoComplete="off"
                className="file-picker-search-input"
                placeholder={t?.searchPlaceholder || 'Поиск файлов...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // ничего, если нет внешнего сабмита
                  }
                  if (e.key === 'Escape') {
                    setSearchTerm('');
                  }
                }}
              />
              {searchTerm && (
                <div
                  onClick={() => setSearchTerm('')}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    position: 'absolute',
                    top: '35%',
                    right: 0,
                    transform: 'translateY(-50%)',
                    padding: 'var(--sadr-font-size-8)',
                    cursor: 'pointer',
                  }}
                >
                  <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path
                      d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
                      fill={isHovered ? '#D30630' : '#000'}
                    ></path>
                  </svg>
                </div>
              )}
            </div>
            <p>{t?.clickToSelectFile || 'Для выбора файла кликните по нему'}</p>
            <div className="file-manager-folder-list">{renderTree(filterTree(files, searchTerm))}</div>
            <div className={`file-manager-confirm-move ${!selectedFile ? 'hidden' : ''}`}>
              <p>
                {t?.addFileConfirmation || 'Добавить файл'} <strong>{selectedFile?.name}</strong>
              </p>
            </div>
            <div className="form-container_submit-button height-unset padding-20">
              <CustomButton
                text={t?.confirmButton || 'Подтвердить'}
                onClick={handleFileConfirm}
                isButton
                disabled={!selectedFile}
              />
              <CustomButton
                text={t?.cancelButton || 'Отмена'}
                onClick={onClose}
                isButton
                color="white"
                border
                colorText="var(--sadr-border)"
              />
            </div>
          </CustomContainer>
        </Modal>
      )}
    </>
  );
};

export default FilePicker;

// import React, { useEffect, useMemo, useState } from 'react';
// import '../../styles/FilePicker.css';
// import '../../styles/MoveFileModal.css';
// import Modal from '../Modal/Modal';
// import CustomContainer from '../CustomElements/CustomContainer';
// import CustomTitle from '../CustomElements/CustomTitle';
// import CustomButton from '../CustomElements/CustomButton';
// import { handleUniversalChange } from '../../utils/formUtils';
// import { useDispatch, useSelector } from 'react-redux';
// import { initFilesFromServer } from '../../redux/actions/files.action';
// import useTranslations from '../../customHook/useTranslations';
// import translations from '../../content/PagesContent/FilesPages/filePageTranslations.json';

// const ChevronRight = () => (
//   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M9 18l6-6-6-6" />
//   </svg>
// );

// const ChevronDown = () => (
//   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M6 9l6 6 6-6" />
//   </svg>
// );

// const FilePicker = ({ label, placeholder, formData, setFormData, fieldIdName, fieldName }) => {
//   const dispatch = useDispatch();
//   const t = useTranslations(translations);
//   const resolvedPlaceholder = useMemo(() => placeholder || t?.filePlaceholder, [placeholder, t]);
//   const files = useSelector((state) => state.files);

//   const [selectedFile, setSelectedFile] = useState('');
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [expandedFolders, setExpandedFolders] = useState({});
//   const [isHovered, setIsHovered] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');

//   useEffect(() => {
//     dispatch(initFilesFromServer());
//   }, [dispatch]);

//   const onClose = () => {
//     setSelectedFile('');
//     setIsModalOpen(false);
//     setSearchTerm('');
//   };

//   const handleFileReset = (event) => {
//     event.stopPropagation();
//     handleUniversalChange(formData, setFormData, null, fieldIdName, { id: '', value: '' }, 'select');
//     handleUniversalChange(formData, setFormData, null, fieldName, { value: '' }, 'select');
//     setSelectedFile('');
//   };

//   const handleFileConfirm = () => {
//     handleUniversalChange(formData, setFormData, null, fieldIdName, { value: selectedFile?.id }, 'select');
//     handleUniversalChange(formData, setFormData, null, fieldName, { value: selectedFile?.name }, 'select');
//     onClose();
//   };

//   const toggleFolder = (folderId) => {
//     setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
//   };

//   const handleFileSelect = (file) => {
//     setSelectedFile(file);
//   };

//   const highlightMatch = (text, query) => {
//     if (!query) return text;
//     const parts = text.split(new RegExp(`(${query})`, 'gi'));
//     return parts.map((part, i) =>
//       part.toLowerCase() === query.toLowerCase() ? <span key={i} className="highlight">{part}</span> : part
//     );
//   };

//   const filterTree = (items, term) => {
//     if (!term.trim()) return items;

//     return items
//       .map((item) => {
//         if (item.type === 'folder') {
//           const filteredChildren = filterTree(item.children || [], term);
//           if (filteredChildren.length > 0) {
//             return { ...item, children: filteredChildren };
//           }
//         }

//         if (item.name.toLowerCase().includes(term.toLowerCase())) {
//           return item;
//         }

//         return null;
//       })
//       .filter(Boolean);
//   };

//   const renderTree = (items, level = 0) =>
//     items.map((item) => (
//       <div key={item.id} style={{ marginLeft: level * 15 }}>
//         <div
//           className={`file-picker-item ${selectedFile?.id === item.id ? 'selected' : ''}`}
//           onClick={item.type === 'file' ? () => handleFileSelect(item) : () => toggleFolder(item.id)}
//         >
//           <span className="file-manager-folder-label">
//             <span className="folder-icon-wrapper">
//               {item.type === 'folder' && (expandedFolders[item.id] ? <ChevronDown /> : <ChevronRight />)}
//               <img
//                 src={`/image/${item.type === 'folder' ? 'folder.png' : 'sheet.png'}`}
//                 alt={item.type === 'folder' ? 'Folder' : 'File'}
//                 className="file-folder-icon img-size"
//               />
//             </span>
//             <span className="folder-name">{highlightMatch(item.name, searchTerm)}</span>
//           </span>
//         </div>
//         {expandedFolders[item.id] && item.children && (
//           <div className="file-manager-nested-folders">{renderTree(item.children, level + 1)}</div>
//         )}
//       </div>
//     ));

//   return (
//     <>
//       <div className="file-picker">
//         {label && <label className="file-picker-label">{label}</label>}
//         <div
//           className="file-picker-container"
//           onClick={() => setIsModalOpen(true)}
//           onMouseEnter={() => setIsHovered(true)}
//           onMouseLeave={() => setIsHovered(false)}
//         >
//           <span
//             className={`file-picker-content ${formData?.[fieldName] || selectedFile ? 'has-value' : ''}`}
//             title={formData?.[fieldName] || selectedFile?.name || resolvedPlaceholder}
//           >
//             {formData?.[fieldName] || selectedFile?.name || resolvedPlaceholder}
//           </span>
//           <div
//             className="file-tree-button"
//             onClick={(event) => {
//               if (formData?.[fieldName]) handleFileReset(event);
//             }}
//           >
//             {!formData?.[fieldName] ? (
//               <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true">
//                 <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
//               </svg>
//             ) : (
//               <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
//                 <path
//                   d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
//                   fill={isHovered ? '#D30630' : '#000'}
//                 ></path>
//               </svg>
//             )}
//           </div>
//         </div>
//       </div>

//       {isModalOpen && (
//         <Modal isOpen={isModalOpen} onClose={onClose} width="650px" height="650px">
//           <CustomContainer>
//             <CustomTitle titleText={t?.selectFileTitle || 'ВЫБЕРИТЕ ФАЙЛ'} />
//             <p>{t?.clickToSelectFile || 'Для выбора файла кликните по нему'}</p>

//             <input
//               type="text"
//               className="file-picker-search-input"
//               placeholder={t?.searchPlaceholder || 'Поиск файлов...'}
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//             />

//             <div className="file-manager-folder-list">
//               {renderTree(filterTree(files, searchTerm))}
//             </div>

//             <div className={`file-manager-confirm-move ${!selectedFile ? 'hidden' : ''}`}>
//               <p>
//                 {t?.addFileConfirmation || 'Добавить файл'} <strong>{selectedFile?.name}</strong>
//               </p>
//             </div>
//             <div className="form-container_submit-button height-unset padding-20">
//               <CustomButton
//                 text={t?.confirmButton || 'Подтвердить'}
//                 onClick={handleFileConfirm}
//                 isButton
//                 disabled={!selectedFile}
//               />
//               <CustomButton
//                 text={t?.cancelButton || 'Отмена'}
//                 onClick={onClose}
//                 isButton
//                 color="white"
//                 border
//                 colorText="var(--sadr-border)"
//               />
//             </div>
//           </CustomContainer>
//         </Modal>
//       )}
//     </>
//   );
// };

// export default FilePicker;
